import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getLiquidacionesComisiones,
} from "../helpers/rutaComisiones";

import logo from "../images/ridersmiami-logo.png";

/* =========================================================
   FORMATOS
   ========================================================= */

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$ ${n.toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value;
  }

  return value._id || value.id || "";
};

const nombreVendedor = (item) => {
  const vendedor = item?.vendedor;

  if (!vendedor) {
    return "-";
  }

  if (typeof vendedor === "string") {
    return vendedor;
  }

  return (
    [
      vendedor.nombres,
      vendedor.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    vendedor.email ||
    "-"
  );
};

/* =========================================================
   COMPONENTE
   ========================================================= */

export default function ReporteComisionesSaldos() {
  const [desde, setDesde] =
    useState("");

  const [hasta, setHasta] =
    useState("");

  const [sede, setSede] =
    useState("");

  const [vendedor, setVendedor] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    liquidaciones,
    setLiquidaciones,
  ] = useState([]);

  const [
    vendedoresBase,
    setVendedoresBase,
  ] = useState([]);

  /* =======================================================
     VENDEDORES DISPONIBLES
     ======================================================= */

  const vendedoresDisponibles =
    useMemo(() => {
      const map = new Map();

      vendedoresBase.forEach(
        (item) => {
          const id =
            getId(item?.vendedor);

          const nombre =
            nombreVendedor(item);

          if (
            id &&
            nombre &&
            !map.has(id)
          ) {
            map.set(id, nombre);
          }
        }
      );

      return Array.from(
        map.entries()
      )
        .map(([id, nombre]) => ({
          id,
          nombre,
        }))
        .sort((a, b) =>
          a.nombre.localeCompare(
            b.nombre,
            "es"
          )
        );
    }, [vendedoresBase]);

  /* =======================================================
     LIQUIDACIONES CON SALDO
     ======================================================= */

  const pendientes =
    useMemo(() => {
      return liquidaciones.filter(
        (item) => {
          const estado =
            String(
              item?.estado || ""
            ).toUpperCase();

          const saldo =
            Number(
              item
                ?.saldoPendienteUSD ||
                0
            );

          return (
            saldo > 0 &&
            estado !== "ANULADA"
          );
        }
      );
    }, [liquidaciones]);

  /* =======================================================
     AGRUPADO POR VENDEDOR + SEDE
     ======================================================= */

  const filas =
    useMemo(() => {
      const map = new Map();

      pendientes.forEach(
        (item) => {
          const vendedorId =
            getId(
              item?.vendedor
            );

          const nombre =
            nombreVendedor(
              item
            );

          const sedeItem =
            item?.sede || "-";

          const clave =
            `${vendedorId}-${sedeItem}`;

          if (!map.has(clave)) {
            map.set(clave, {
              vendedorId,
              vendedor:
                nombre,

              sede:
                sedeItem,

              cantidadLiquidaciones:
                0,

              totalLiquidadoUSD:
                0,

              totalPagadoUSD:
                0,

              saldoPendienteUSD:
                0,
            });
          }

          const grupo =
            map.get(clave);

          grupo.cantidadLiquidaciones +=
            1;

          grupo.totalLiquidadoUSD +=
            Number(
              item
                ?.totalLiquidacionUSD ||
                0
            );

          grupo.totalPagadoUSD +=
            Number(
              item
                ?.totalPagadoUSD ||
                0
            );

          grupo.saldoPendienteUSD +=
            Number(
              item
                ?.saldoPendienteUSD ||
                0
            );
        }
      );

      return Array.from(
        map.values()
      )
        .map((item) => ({
          ...item,

          totalLiquidadoUSD:
            Number(
              item
                .totalLiquidadoUSD
                .toFixed(2)
            ),

          totalPagadoUSD:
            Number(
              item
                .totalPagadoUSD
                .toFixed(2)
            ),

          saldoPendienteUSD:
            Number(
              item
                .saldoPendienteUSD
                .toFixed(2)
            ),
        }))
        .sort(
          (a, b) =>
            b.saldoPendienteUSD -
            a.saldoPendienteUSD
        );
    }, [pendientes]);

  /* =======================================================
     TOTALES
     ======================================================= */

  const totales =
    useMemo(() => {
      return filas.reduce(
        (acc, item) => {
          acc.vendedoresConSaldo +=
            1;

          acc.liquidacionesPendientes +=
            item
              .cantidadLiquidaciones;

          acc.totalLiquidadoUSD +=
            item
              .totalLiquidadoUSD;

          acc.totalPagadoUSD +=
            item
              .totalPagadoUSD;

          acc.saldoPendienteUSD +=
            item
              .saldoPendienteUSD;

          return acc;
        },
        {
          vendedoresConSaldo: 0,
          liquidacionesPendientes: 0,
          totalLiquidadoUSD: 0,
          totalPagadoUSD: 0,
          saldoPendienteUSD: 0,
        }
      );
    }, [filas]);

  /* =======================================================
     CARGAR
     ======================================================= */

  const cargarReporte = async (
    cargaInicial = false
  ) => {
    setLoading(true);
    setError("");

    try {
      const data =
        await getLiquidacionesComisiones({
          desde,
          hasta,
          sede,
          vendedor,
        });

      const registros =
        Array.isArray(
          data?.liquidaciones
        )
          ? data.liquidaciones
          : [];

      setLiquidaciones(
        registros
      );

      if (cargaInicial) {
        setVendedoresBase(
          registros
        );
      }
    } catch (err) {
      console.error(
        "Error informe saldos comisiones:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo cargar el informe."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     LIMPIAR
     ======================================================= */

  const limpiarFiltros = () => {
    setDesde("");
    setHasta("");
    setSede("");
    setVendedor("");
  };

  /* =======================================================
     CSV
     ======================================================= */

  const generarCSV = () => {
    if (filas.length === 0) {
      return;
    }

    const encabezados = [
      "Vendedor",
      "Sede",
      "Liquidaciones pendientes",
      "Total liquidado USD",
      "Total pagado USD",
      "Saldo pendiente USD",
    ];

    const escapar = (value) => {
      const texto =
        String(value ?? "");

      return `"${texto.replace(
        /"/g,
        '""'
      )}"`;
    };

    const registros =
      filas.map(
        (item) => [
          item.vendedor,
          item.sede,
          item
            .cantidadLiquidaciones,
          item
            .totalLiquidadoUSD,
          item
            .totalPagadoUSD,
          item
            .saldoPendienteUSD,
        ]
      );

    const contenido = [
      encabezados,
      ...registros,
    ]
      .map((fila) =>
        fila
          .map(escapar)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(
      [
        "\uFEFF",
        contenido,
      ],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.download =
      "saldos-comisiones-riders.csv";

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };

  /* =======================================================
     PDF
     ======================================================= */

  const generarPDF = async () => {
    try {
      const doc =
        new jsPDF(
          "landscape",
          "mm",
          "a4"
        );

      const logoImg =
        await cargarImagen(
          logo
        );

      doc.addImage(
        logoImg,
        "PNG",
        10,
        8,
        34,
        12
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(16);

      doc.text(
        "Informe de Saldos Pendientes de Comisiones",
        50,
        14
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(10);

      doc.text(
        "Riders Miami",
        50,
        20
      );

      doc.text(
        `Desde: ${
          desde || "Todas"
        }   Hasta: ${
          hasta || "Todas"
        }`,
        287,
        14,
        {
          align: "right",
        }
      );

      const vendedorNombre =
        vendedoresDisponibles.find(
          (item) =>
            item.id === vendedor
        )?.nombre || "Todos";

      doc.text(
        `Sede: ${
          sede || "Todas"
        } | Vendedor: ${vendedorNombre}`,
        287,
        20,
        {
          align: "right",
        }
      );

      doc.setDrawColor(180);

      doc.line(
        10,
        24,
        287,
        24
      );

      /* RESUMEN */

      const resumenY = 28;
      const boxW = 62;
      const boxH = 14;
      const gap = 4;

      const itemsResumen = [
        {
          label:
            "Vendedores con saldo",
          value:
            String(
              totales
                .vendedoresConSaldo
            ),
        },
        {
          label:
            "Liquidaciones pendientes",
          value:
            String(
              totales
                .liquidacionesPendientes
            ),
        },
        {
          label:
            "Total pagado",
          value:
            formatUSDPdf(
              totales
                .totalPagadoUSD
            ),
        },
        {
          label:
            "Total adeudado",
          value:
            formatUSDPdf(
              totales
                .saldoPendienteUSD
            ),
        },
      ];

      itemsResumen.forEach(
        (item, i) => {
          const x =
            10 +
            i *
              (boxW + gap);

          doc.setDrawColor(
            210
          );

          doc.roundedRect(
            x,
            resumenY,
            boxW,
            boxH,
            1.5,
            1.5
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(8);

          doc.text(
            item.label,
            x + 2,
            resumenY + 4.5
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(10);

          doc.text(
            String(
              item.value
            ),
            x + 2,
            resumenY + 10
          );
        }
      );

      const body =
        filas.map(
          (item) => [
            item.vendedor,

            item.sede,

            String(
              item
                .cantidadLiquidaciones
            ),

            formatUSDPdf(
              item
                .totalLiquidadoUSD
            ),

            formatUSDPdf(
              item
                .totalPagadoUSD
            ),

            formatUSDPdf(
              item
                .saldoPendienteUSD
            ),
          ]
        );

      autoTable(doc, {
        startY: 48,

        theme: "grid",

        margin: {
          left: 15,
          right: 15,
        },

        head: [
          [
            "Vendedor",
            "Sede",
            "Liquidaciones",
            "Liquidado",
            "Pagado",
            "Saldo pendiente",
          ],
        ],

        body,

        foot: [
          [
            "Totales",
            "",
            String(
              totales
                .liquidacionesPendientes
            ),

            formatUSDPdf(
              totales
                .totalLiquidadoUSD
            ),

            formatUSDPdf(
              totales
                .totalPagadoUSD
            ),

            formatUSDPdf(
              totales
                .saldoPendienteUSD
            ),
          ],
        ],

        styles: {
          fontSize: 8,
          cellPadding: 1.4,
          valign: "middle",
        },

        headStyles: {
          fillColor: [
            33,
            37,
            41,
          ],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },

        footStyles: {
          fillColor: [
            245,
            245,
            245,
          ],
          textColor: 20,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 52,
          },

          1: {
            cellWidth: 30,
          },

          2: {
            cellWidth: 32,
            halign: "center",
          },

          3: {
            cellWidth: 42,
            halign: "right",
          },

          4: {
            cellWidth: 42,
            halign: "right",
          },

          5: {
            cellWidth: 45,
            halign: "right",
          },
        },

        didDrawPage: () => {
          const pageCount =
            doc.internal
              .getNumberOfPages();

          doc.setFontSize(8);

          doc.setTextColor(100);

          doc.text(
            `Página ${pageCount}`,
            doc.internal.pageSize
              .getWidth() - 10,
            doc.internal.pageSize
              .getHeight() - 6,
            {
              align: "right",
            }
          );
        },
      });

      doc.save(
        "saldos-pendientes-comisiones-riders.pdf"
      );
    } catch (error) {
      console.error(
        "Error generando PDF:",
        error
      );

      alert(
        "No se pudo generar el PDF."
      );
    }
  };

  /* =======================================================
     VISTA
     ======================================================= */

  return (
    <div className="container-fluid mt-3 mb-4">

      <Card className="shadow-sm border-0">

        <Card.Body>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">

            <div>
              <h4 className="mb-0">
                Saldos Pendientes
                de Comisiones
              </h4>

              <small className="text-muted">
                Riders Miami
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={
                  limpiarFiltros
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={() =>
                  cargarReporte(false)
                }
                disabled={loading}
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={generarCSV}
                disabled={
                  loading ||
                  filas.length === 0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={
                  loading ||
                  filas.length === 0
                }
              >
                PDF
              </Button>

            </div>
          </div>

          {/* FILTROS */}

          <Row className="g-2 mb-2 align-items-end">

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                value={desde}
                onChange={(e) =>
                  setDesde(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) =>
                  setHasta(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Sede
              </Form.Label>

              <Form.Select
                value={sede}
                onChange={(e) =>
                  setSede(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todas
                </option>

                <option value="TUCUMAN">
                  Tucumán
                </option>

                <option value="YERBA_BUENA">
                  Yerba Buena
                </option>

                <option value="MIAMI">
                  Miami
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Vendedor
              </Form.Label>

              <Form.Select
                value={vendedor}
                onChange={(e) =>
                  setVendedor(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                {vendedoresDisponibles.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.nombre}
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

          </Row>

          <div className="small text-muted mb-3">
            Se muestran únicamente
            liquidaciones con saldo
            pendiente mayor a cero.
          </div>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* RESUMEN */}

          <Row className="g-2 mb-3">

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Vendedores con saldo
                  </small>

                  <h5 className="mb-0">
                    {
                      totales
                        .vendedoresConSaldo
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Liquidaciones pendientes
                  </small>

                  <h5 className="mb-0">
                    {
                      totales
                        .liquidacionesPendientes
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Total pagado
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      totales
                        .totalPagadoUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Total adeudado
                  </small>

                  <h5 className="mb-0 text-warning">
                    {formatUSD(
                      totales
                        .saldoPendienteUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

          </Row>

          {/* TABLA */}

          <div
            className="table-responsive"
            style={{
              maxHeight: "65vh",
            }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
            >

              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>

                  <th>
                    Vendedor
                  </th>

                  <th>
                    Sede
                  </th>

                  <th className="text-center">
                    Liquidaciones
                  </th>

                  <th className="text-end">
                    Liquidado
                  </th>

                  <th className="text-end">
                    Pagado
                  </th>

                  <th className="text-end">
                    Saldo pendiente
                  </th>

                  <th className="text-center">
                    Estado
                  </th>

                </tr>
              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />

                      Cargando informe...
                    </td>
                  </tr>
                ) : filas.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-4 text-muted"
                    >
                      No existen saldos
                      pendientes para los
                      filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filas.map(
                    (item) => (
                      <tr
                        key={`${item.vendedorId}-${item.sede}`}
                      >

                        <td className="fw-semibold">
                          {
                            item.vendedor
                          }
                        </td>

                        <td>
                          {
                            item.sede
                          }
                        </td>

                        <td className="text-center">
                          {
                            item
                              .cantidadLiquidaciones
                          }
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .totalLiquidadoUSD
                          )}
                        </td>

                        <td className="text-end text-success">
                          {formatUSD(
                            item
                              .totalPagadoUSD
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .saldoPendienteUSD
                          )}
                        </td>

                        <td className="text-center">
                          <Badge bg="warning">
                            Pendiente
                          </Badge>
                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

              {filas.length > 0 && (
                <tfoot>
                  <tr>

                    <th colSpan="2">
                      Totales
                    </th>

                    <th className="text-center">
                      {
                        totales
                          .liquidacionesPendientes
                      }
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .totalLiquidadoUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .totalPagadoUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .saldoPendienteUSD
                      )}
                    </th>

                    <th />

                  </tr>
                </tfoot>
              )}

            </Table>
          </div>

        </Card.Body>
      </Card>
    </div>
  );
}